import { createContext, useState, ReactNode, useContext } from "react";
import { VerifyEmailBottomSheet } from "@/components/auth/VerifyEmailBottomSheet";
import { useSendEmailVerification } from "@/hooks/api/useAuth";
import { useToast } from "@/components/shared/toast/Toast";

interface VerificationModalContextType {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  isLoading: boolean;
  error: string | null;
  successMessage: string | null;
}

const VerificationModalContext = createContext<VerificationModalContextType | undefined>(undefined);

export function VerificationModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { mutate: sendVerification } = useSendEmailVerification();
  const { addToast } = useToast();

  const open = () => {
    setIsOpen(true);
    // Clear any previous states when opening
    setError(null);
    setSuccessMessage(null);
  };

  const close = () => {
    setIsOpen(false);
    // Clear states when closing
    setError(null);
    setSuccessMessage(null);
    setIsLoading(false);
  };

  const handleResend = () => {
    if (isLoading) return; // Prevent multiple clicks

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    // Get current path to use as continue URL
    const continueUrl = window.location.pathname + window.location.search;

    sendVerification(
      { continueUrl },
      {
        onSuccess: () => {
          setIsLoading(false);
          addToast("Verification email sent! Please check your inbox.", "success", 5000, "top-full");
          close(); // Close the modal since we're treating this as success
        },
        onError: (error) => {
          setIsLoading(false);
          const errorMessage = error.message || "";

          // Check for "too many attempts" error and treat it as success
          if (errorMessage.toLowerCase().includes("too_many_attempts") ||
            errorMessage.toLowerCase().includes("too many attempts") ||
            (error as any).code === 'auth/too-many-requests') {
            addToast("Verification email sent! Please check your inbox.", "success", 5000, "top-full");
            close(); // Close the modal since we're treating this as success
          } else {
            setError(errorMessage || "Failed to send verification email. Please try again.");
          }
        },
      }
    );
  };

  return (
    <VerificationModalContext.Provider value={{
      isOpen,
      open,
      close,
      isLoading,
      error,
      successMessage
    }}>
      {children}
      <VerifyEmailBottomSheet
        open={isOpen}
        onClose={close}
        onResend={handleResend}
        isLoading={isLoading}
        error={error}
        successMessage={successMessage}
      />
    </VerificationModalContext.Provider>
  );
}

export function useVerificationModal() {
  const context = useContext(VerificationModalContext);
  if (!context) {
    throw new Error("useVerificationModal must be used within VerificationModalProvider");
  }
  return context;
} 